import React, { useState } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { useAuth } from './AuthContext';
import EditProduct from './EditProduct';
import JSZip from 'jszip';
import './ProductDetail.css';

function ProductDetail({ producto, onClose }) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { isAdmin } = useAuth();

  const copiarDescripcion = async () => {
    try {
      const texto = `${producto.marca} ${producto.modelo}
L ${producto.precio ? producto.precio.toLocaleString('es-HN') : 'N/A'}
Estado: ${producto.estado || 'N/A'}
Almacenamiento: ${producto.almacenamiento || 'N/A'}
Color: ${producto.color || 'N/A'}
Cantidad disponible: ${producto.cantidad || 0}


${producto.descripcion || ''}`;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(texto);
        alert('✅ Descripción copiada al portapapeles');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = texto;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          alert('✅ Descripción copiada al portapapeles');
        } catch (err) {
          alert('❌ Error al copiar. Intenta de nuevo.');
        }
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Error copiando:', error);
      alert('❌ Error al copiar descripción');
    }
  };

  const compartirWhatsApp = () => {
    const mensaje = `*${producto.marca} ${producto.modelo}*

💰 Precio: L ${producto.precio ? producto.precio.toLocaleString('es-HN') : 'N/A'}
📦 Estado: ${producto.estado || 'N/A'}
💾 Almacenamiento: ${producto.almacenamiento || 'N/A'}
🎨 Color: ${producto.color || 'N/A'}
📊 Disponibles: ${producto.cantidad || 0} unidad${producto.cantidad !== 1 ? 'es' : ''}

${producto.descripcion || ''}

📸 Ver fotos y detalles:
https://jasaminventario.com/producto/${producto.id}`;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      const url = `whatsapp://send?text=${encodeURIComponent(mensaje)}`;
      window.location.href = url;
    } else {
      const url = `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
      window.open(url, '_blank');
    }
  };

  const descargarImagenActual = async () => {
    if (!producto.imageURLs || !producto.imageURLs[selectedImageIndex]) {
      alert('❌ No hay imagen para descargar');
      return;
    }

    try {
      const url = producto.imageURLs[selectedImageIndex];
      const response = await fetch(url);
      const blob = await response.blob();
      
      const nombreProducto = `${producto.marca}_${producto.modelo}`.replace(/\s+/g, '_');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${nombreProducto}_${selectedImageIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error descargando imagen:', error);
      alert('❌ Error al descargar la imagen');
    }
  };

  const descargarTodasImagenes = async () => {
    if (!producto.imageURLs || producto.imageURLs.length === 0) {
      alert('❌ No hay imágenes para descargar');
      return;
    }

    setDownloading(true);

    try {
      const zip = new JSZip();
      const nombreProducto = `${producto.marca}_${producto.modelo}`.replace(/\s+/g, '_');
      
      for (let i = 0; i < producto.imageURLs.length; i++) {
        const url = producto.imageURLs[i];
        
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          zip.file(`${nombreProducto}_${i + 1}.jpg`, blob);
        } catch (error) {
          console.error(`Error descargando imagen ${i + 1}:`, error);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${nombreProducto}_imagenes.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      alert(`✅ ZIP descargado con ${producto.imageURLs.length} imágenes`);
      setDownloading(false);
    } catch (error) {
      console.error('Error creando ZIP:', error);
      alert('❌ Error al crear el ZIP. Intenta de nuevo.');
      setDownloading(false);
    }
  };

  const nextImage = () => {
    if (selectedImageIndex < producto.imageURLs.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const reducirStock = async () => {
    if (producto.cantidad <= 0) {
      alert('⚠️ No hay stock disponible');
      return;
    }

    const nuevoStock = producto.cantidad - 1;
    const confirmar = window.confirm(`¿Reducir stock de "${producto.marca} ${producto.modelo}"?\n\nStock actual: ${producto.cantidad}\nNuevo stock: ${nuevoStock}`);
    
    if (!confirmar) return;

    try {
      const productoRef = doc(db, 'productos', producto.id);
      await updateDoc(productoRef, {
        cantidad: nuevoStock,
        disponible: nuevoStock > 0,
        updatedAt: new Date()
      });
      alert(nuevoStock === 0 
        ? '✅ Stock reducido a 0. Producto marcado como NO DISPONIBLE' 
        : '✅ Stock reducido correctamente'
      );
      window.location.reload();
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al reducir stock');
    }
  };

  const aumentarStock = async () => {
    const nuevoStock = producto.cantidad + 1;
    const confirmar = window.confirm(`¿Aumentar stock de "${producto.marca} ${producto.modelo}"?\n\nStock actual: ${producto.cantidad}\nNuevo stock: ${nuevoStock}`);
    
    if (!confirmar) return;

    try {
      const productoRef = doc(db, 'productos', producto.id);
      await updateDoc(productoRef, {
        cantidad: nuevoStock,
        disponible: true,
        updatedAt: new Date()
      });
      alert('✅ Stock aumentado correctamente');
      window.location.reload();
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al aumentar stock');
    }
  };

  const toggleDisponibilidad = async () => {
    try {
      const productoRef = doc(db, 'productos', producto.id);
      await updateDoc(productoRef, {
        disponible: !producto.disponible,
        updatedAt: new Date()
      });
      alert('✅ Disponibilidad actualizada');
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al actualizar');
    }
  };

  const eliminarProducto = async () => {
    try {
      for (const url of producto.imageURLs) {
        try {
          const imageRef = ref(storage, url);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error eliminando imagen:', error);
        }
      }

      await deleteDoc(doc(db, 'productos', producto.id));
      alert('✅ Producto eliminado');
      onClose();
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al eliminar producto');
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={onClose}>✕</button>

          <div className="detail-container">
            <div className="detail-images">
              <div className="main-image-container">
                {producto.imageURLs && producto.imageURLs[selectedImageIndex] ? (
                  <>
                    <img 
                      src={producto.imageURLs[selectedImageIndex]} 
                      alt={producto.modelo}
                      className="main-image-large"
                    />
                    
                    {producto.imageURLs.length > 1 && (
                      <>
                        <button 
                          className="nav-arrow nav-arrow-left"
                          onClick={prevImage}
                          disabled={selectedImageIndex === 0}
                        >
                          ‹
                        </button>
                        <button 
                          className="nav-arrow nav-arrow-right"
                          onClick={nextImage}
                          disabled={selectedImageIndex === producto.imageURLs.length - 1}
                        >
                          ›
                        </button>
                      </>
                    )}

                    {producto.imageURLs.length > 1 && (
                      <div className="image-dots">
                        {producto.imageURLs.map((_, index) => (
                          <span
                            key={index}
                            className={`dot ${index === selectedImageIndex ? 'active' : ''}`}
                            onClick={() => setSelectedImageIndex(index)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-image-large">📱</div>
                )}
              </div>

              {producto.imageURLs && producto.imageURLs.length > 1 && (
                <div className="image-thumbnails">
                  {producto.imageURLs.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`${producto.modelo} ${index + 1}`}
                      className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                      onClick={() => setSelectedImageIndex(index)}
                    />
                  ))}
                </div>
              )}

              {producto.imageURLs && producto.imageURLs.length > 0 && (
                <button className="btn-download-current" onClick={descargarImagenActual}>
                  🖼️ Descargar Imagen
                </button>
              )}
            </div>

            <div className="detail-info">
              <div className="detail-header">
                <h2>{producto.marca} {producto.modelo}</h2>
                <div className="badges-detail">
                  {producto.disponible ? (
                    <span className="badge disponible">Disponible</span>
                  ) : (
                    <span className="badge no-disponible">No disponible</span>
                  )}
                  {producto.cantidad >= 0 && (
                    <span className={`cantidad-badge ${
                      producto.cantidad === 0 ? 'agotado' :
                      producto.cantidad <= 2 ? 'pocas' : 
                      producto.cantidad <= 5 ? 'medias' : 'muchas'
                    }`}>
                      {producto.cantidad} unidad{producto.cantidad !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>
              </div>

              <p className="detail-price">L {producto.precio ? producto.precio.toLocaleString('es-HN') : 'N/A'}</p>

              <div className="detail-specs">
                <div className="spec-item">
                  <span className="spec-label">Estado:</span>
                  <span className="spec-value">{producto.estado || 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Almacenamiento:</span>
                  <span className="spec-value">{producto.almacenamiento || 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Color:</span>
                  <span className="spec-value">{producto.color || 'N/A'}</span>
                </div>
              </div>

              <div className="detail-description">
                <h3>Descripción</h3>
                <p>{producto.descripcion || 'Sin descripción'}</p>
              </div>

              <div className="detail-actions">
                <button className="btn-primary" onClick={copiarDescripcion}>
                  📋 Copiar Descripción
                </button>

                <button className="btn-primary btn-whatsapp" onClick={compartirWhatsApp}>
                  💬 Compartir por WhatsApp
                </button>

                <button 
                  className="btn-primary" 
                  onClick={descargarTodasImagenes}
                  disabled={downloading}
                >
                  {downloading 
                    ? `📦 Creando ZIP...` 
                    : `📦 Descargar ZIP (${producto.imageURLs?.length || 0})`
                  }
                </button>

                {isAdmin() && (
                  <>
                    <div className="stock-controls">
                      <button 
                        className="btn-stock btn-minus" 
                        onClick={reducirStock}
                        disabled={producto.cantidad <= 0}
                      >
                        ➖ Reducir Stock
                      </button>
                      <span className="stock-display">
                        Stock: <strong>{producto.cantidad}</strong>
                      </span>
                      <button 
                        className="btn-stock btn-plus" 
                        onClick={aumentarStock}
                      >
                        ➕ Aumentar Stock
                      </button>
                    </div>

                    <button className="btn-primary" onClick={() => setShowEdit(true)}>
                      ✏️ Editar Producto
                    </button>

                    <button className="btn-warning" onClick={toggleDisponibilidad}>
                      {producto.disponible ? '🚫 Marcar No Disponible' : '✅ Marcar Disponible'}
                    </button>

                    <button className="btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                      🗑️ Eliminar Producto
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {showDeleteConfirm && (
            <div className="confirm-dialog">
              <div className="confirm-content">
                <h3>⚠️ Confirmar Eliminación</h3>
                <p>¿Estás seguro de eliminar este producto?</p>
                <p><strong>Esta acción no se puede deshacer.</strong></p>
                <div className="confirm-buttons">
                  <button onClick={() => setShowDeleteConfirm(false)}>Cancelar</button>
                  <button className="btn-danger" onClick={eliminarProducto}>Eliminar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEdit && (
        <EditProduct 
          producto={producto} 
          onClose={() => setShowEdit(false)}
          onUpdate={() => {}}
        />
      )}
    </>
  );
}

export default ProductDetail;